import React, { useState } from "react";
import { Form, Input, Button, Modal, Spin } from "antd";
import { useAppSelector } from "../../store/Hooks";
import { useMutation } from "react-query";
import { AgencyService } from "../../services/AgencyService";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlSendConnectionRequestMailRequest } from "../../models/service-models/agency/SendConnectionRequestMailRequest";

export type AgencyContactProps = {
    hotelId: string;
    agencyId: string;
    agencyName?: string;
    callback?: () => void;
};

const AgencyContact = ({ hotelId, callback, agencyId, agencyName }: AgencyContactProps) => {
    const user = useAppSelector((state) => state.site.user);
    const [form] = Form.useForm();
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState("");
    const userHotels = useAppSelector((state) => state.site.userHotels);
    const { t } = useTranslation();

    const handlePreview = () => {
        const { hotelName, name, depName, phone, email, market } = form.getFieldsValue();

        const mailContent = `
            <div style="font-family: Arial, sans-serif; padding: 20px">
                <p><strong>Sayın ${agencyName},</strong></p>
                <p>Acentenizle anlaşma yapmak için iletişime geçmek isteriz. Anlaşma sağlamak istediğimiz marketler ve iletişim bilgilerimizi bilgilerinize sunar, nazik dönüşünüzü rica ederiz.</p>
                <hr />
                <p><strong>${t("agencies.contactForm.hotel")}:</strong> ${hotelName || ""}</p>
                <p><strong>${t("agencies.contactForm.nameSurname")}:</strong> ${name || ""}</p>
                <p><strong>${t("agencies.contactForm.department")}:</strong> ${depName || ""}</p>
                <p><strong>${t("agencies.contactForm.phone")}:</strong> ${phone || ""}</p>
                <p><strong>${t("agencies.contactForm.email")}:</strong> ${email || ""}</p>
                <p><strong>${t("agencies.contactForm.market")}:</strong> ${market || ""}</p>
            </div>
        `;

        setPreviewContent(mailContent);
        setPreviewVisible(true);
    };

    const { mutateAsync: sendConnectionRequest, isLoading: mutateLoading } = useMutation(
        (data: mdlSendConnectionRequestMailRequest) => AgencyService.SendConnectionRequestMail(data),
        {
            onError: (error) => {
                console.error(error);
                toast.error(t("common.errorSaving"));
            },
            onSuccess: (data) => {
                if (data.header?.success && data.body?.isSuccessful) {
                    toast.success(t("common.success"));
                    callback?.();
                } else {
                    toast.error(
                        (data.header?.messages && data.header.messages?.length > 0)
                            ? ObjectHelper.getErrorMessage(data.header?.messages)
                            : data.body?.message
                    );
                }
            },
        }
    );

    return (
        <Spin spinning={mutateLoading}>
            <Form
                form={form}
                layout="vertical"
                onFinish={sendConnectionRequest}
                initialValues={{
                    name: `${user?.firstName || ""} ${user?.lastName || ""}`,
                    phone: user?.phone || user?.mobile || "",
                    email: user?.email || "",
                    hotelName: userHotels?.find((h) => h.id === hotelId)?.localName || t("agencies.contactForm.hotelNotFound"),
                    agencyId: agencyId,
                    hotelId: hotelId                    
                }}
            >
                <Form.Item label={t("agencies.contactForm.nameSurname")} name="name" rules={[{ required: true, message: t("agencies.contactForm.errorName") }]}>
                    <Input placeholder={t("agencies.contactForm.placeholderNameSurname")} />
                </Form.Item>
                <Form.Item label={t("agencies.contactForm.department")} name="depName">
                    <Input placeholder={t("agencies.contactForm.placeholderDepartment")} />
                </Form.Item>
                <Form.Item label={t("agencies.contactForm.phone")} name="phone" rules={[
                    { pattern: /^\+?[0-9]+$/, message: t("agencies.contactForm.errorPhone") }
                ]}>
                    <Input placeholder={t("agencies.contactForm.placeholderPhone")} />
                </Form.Item>
                <Form.Item label={t("agencies.contactForm.email")} name="email" rules={[
                    { required: true, message: t("agencies.contactForm.errorEmail") },
                    { type: "email", message: t("agencies.contactForm.invalidEmail") }
                ]}>
                    <Input placeholder={t("agencies.contactForm.placeholderEmail")} />
                </Form.Item>
                <Form.Item label={t("agencies.contactForm.market")} name="market">
                    <Input placeholder={t("agencies.contactForm.placeholderMarket")} />
                </Form.Item>
                <Form.Item name="hotelName" className="d-none">
                </Form.Item>
                <Form.Item name="agencyId" className="d-none">
                </Form.Item>
                <Form.Item name="hotelId" className="d-none">
                </Form.Item>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <Button type="default" onClick={handlePreview}>{t("agencies.contactForm.preview")}</Button>
                    <Button type="primary" htmlType="submit">{t("agencies.contactForm.submit")}</Button>
                </div>
            </Form>

            <Modal title={t("agencies.contactForm.requestTitle")} visible={previewVisible} onCancel={() => setPreviewVisible(false)} footer={null}>
                <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </Modal>
        </Spin>
    );
};

export default AgencyContact;